import { useFeatureFlag } from "contexts/FeatureFlagContext";
import React, { useCallback, useMemo } from "react";
import { useFindingDetails } from "./FindingDetailsProvider";
import {
  FINDING_ACTIONS,
  FINDING_STATUS,
  PROPOSE_MITIGATED_SUPPORTED_TOOLS,
} from "../Constants";
import { PROPOSED_ACTION_MAP, getActionType } from "../FindingsReview";
import { hasPermission } from "utils/authService";
import _ from "lodash";
import { PermissionSetMap } from "constants/RBACConstants";
import { isCustomFinding } from "../utils";
import { Dropdown, Menu } from "antd";
import Vocabulary from "components/Vocabulary";
import { useGlobalSettingsContext } from "contexts/GlobalSettingsContext";
import { SCAN_EXECUTE_SUPPORTED_TOOL } from "views/ToolSettings/Settings/SettingStatusConstant";
import message from "components/CustomMessage";
import ToolsIntegrationsAPI from "restAPI/ToolsIntegrationAPI";
import ACButtonGroup, { ACButtonGroupItem } from "ac-components/ACButtonGroup";
import { Tooltip } from "antd-v5";
import MultiTicketIcon from "ac-components/ac-icons/MultiTicketIcon";
import RefreshIcon from "ac-components/ac-icons/RefreshIcon";
import MenuIcon from "ac-components/ac-icons/MenuIcon";

export default function FindingDetailsAction() {
  const featureFlags = useFeatureFlag();
  const { finding, loading, bucket, onUpdate, history } = useFindingDetails();
  const { disabledFindingStatus } = useGlobalSettingsContext();

  const getProposalProps = useCallback(
    (proposalType) => {
      let actionObj;
      _.some(finding?.proposedData, (data) => {
        let key = proposalType;
        if (
          data.action === FINDING_ACTIONS.PROPOSE_ACTION &&
          [
            FINDING_ACTIONS.PROPOSE_FALSE_POSITIVE,
            FINDING_ACTIONS.ACCEPT_RISK,
            FINDING_ACTIONS.SUPPRESSED,
            FINDING_ACTIONS.TRIAGE,
            FINDING_ACTIONS.IN_PROGRESS,
          ].includes(key)
        ) {
          key = data.proposedValue;
        }
        const actionType = getActionType(data);
        const isProposed = actionType.type === key;
        if (isProposed) {
          actionObj = actionType;
        }
        return isProposed;
      });
      return {
        disabled: !_.isEmpty(actionObj),
        tooltip: !hasPermission(PermissionSetMap.PROPOSE)
          ? "No Access"
          : actionObj
          ? "Proposal is under review."
          : "",
      };
    },
    [finding?.proposedData]
  );

  const setFindingsAndRedirect = (path) => {
    bucket.addFindings([finding.id], [finding]);
    history.push(path);
  };

  const isAssignProjectDisabled = !(
    hasPermission(PermissionSetMap.UPDATE_FINDINGS_PROJECT) &&
    hasPermission(PermissionSetMap.UPDATE_PROJECT)
  );

  const isAddToAssessmentDisabled = !hasPermission(PermissionSetMap.UPDATE_FINDINGS_ASSESSMENT);

  const isDetachTicketDisabled =
    !hasPermission(PermissionSetMap.DELETE_TICKETS) ||
    _.isEmpty(finding?.armorcodeTicket);

  const isProposeFindingsDisabled = !hasPermission(PermissionSetMap.PROPOSE);
  const isAddToExceptionDisabled = !hasPermission(
    PermissionSetMap.UPDATE_FINDINGS_RISK_REGISTER
  );

  const isFindingSourceJira = finding?.testType === "JIRA";
  const isCreateTicketDisabled = !hasPermission(PermissionSetMap.CREATE_TICKETS) || isFindingSourceJira;

  const proposeFalsePositiveActions = useMemo(() => {
    // eslint-disable-next-line prefer-const
    let { disabled, tooltip } = getProposalProps("PROPOSE_FALSE_POSITIVE");
    disabled = isProposeFindingsDisabled || disabled;
    return { disabled, tooltip };
  }, [isProposeFindingsDisabled, getProposalProps]);

  const proposeAcceptRiskActions = useMemo(() => {
    // eslint-disable-next-line prefer-const
    let { disabled, tooltip } = getProposalProps("PROPOSE_ACCEPTRISK");
    disabled = isProposeFindingsDisabled || disabled;
    return { disabled, tooltip };
  }, [isProposeFindingsDisabled, getProposalProps]);

  const proposeMitigatedActions = useMemo(() => {
    let { disabled } = getProposalProps("PROPOSE_MITIGATED");
    disabled =
      isProposeFindingsDisabled ||
      (isCustomFinding(finding?.testType) && disabled) ||
      (!isCustomFinding(finding?.testType) &&
        !PROPOSE_MITIGATED_SUPPORTED_TOOLS.includes(finding?.testType));
    return { disabled };
  }, [isProposeFindingsDisabled, getProposalProps, finding?.testType]);

  const proposeSuppressedActions = useMemo(() => {
    // eslint-disable-next-line prefer-const
    let { disabled, tooltip } = getProposalProps("PROPOSE_SUPPRESSED");
    disabled = isProposeFindingsDisabled || disabled;
    return { disabled, tooltip };
  }, [getProposalProps, isProposeFindingsDisabled]);

  const proposeTriageActions = useMemo(() => {
    // eslint-disable-next-line prefer-const
    let { disabled, tooltip } = getProposalProps("PROPOSE_TRIAGE");
    disabled = isProposeFindingsDisabled || disabled;
    return { disabled, tooltip };
  }, [getProposalProps, isProposeFindingsDisabled]);

  const proposeInProgressActions = useMemo(() => {
    // eslint-disable-next-line prefer-const
    let { disabled, tooltip } = getProposalProps("PROPOSE_IN_PROGRESS");
    disabled = isProposeFindingsDisabled || disabled;
    return { disabled, tooltip };
  }, [getProposalProps, isProposeFindingsDisabled]);

  const proposeControlledActions = useMemo(() => {
    // eslint-disable-next-line prefer-const
    let { disabled, tooltip } = getProposalProps(
      FINDING_ACTIONS.PROPOSE_CONTROLLED
    );
    disabled = isProposeFindingsDisabled || disabled;
    return { disabled, tooltip };
  }, [getProposalProps, isProposeFindingsDisabled]);

  const proposeCategoryActions = useMemo(() => {
    // eslint-disable-next-line prefer-const
    let { disabled, tooltip } = getProposalProps("PROPOSE_CATEGORY");
    disabled = isProposeFindingsDisabled || disabled;
    return { disabled, tooltip };
  }, [getProposalProps, isProposeFindingsDisabled]);

  const proposeSeverityActions = useMemo(() => {
    // eslint-disable-next-line prefer-const
    let { disabled, tooltip } = getProposalProps("PROPOSE_SEVERITY");
    disabled = isProposeFindingsDisabled || disabled;
    return { disabled, tooltip };
  }, [getProposalProps, isProposeFindingsDisabled]);

  const proposeDueDateActions = useMemo(() => {
    // eslint-disable-next-line prefer-const
    let { disabled, tooltip } = getProposalProps("PROPOSE_DUE_DATE");
    disabled = isProposeFindingsDisabled || disabled;
    return { disabled, tooltip };
  }, [getProposalProps, isProposeFindingsDisabled]);

  const proposeAssigneeActions = useMemo(() => {
    // eslint-disable-next-line prefer-const
    let { disabled, tooltip } = getProposalProps("PROPOSE_ASSIGNEE");
    disabled = isProposeFindingsDisabled || disabled;
    return { disabled, tooltip };
  }, [getProposalProps, isProposeFindingsDisabled]);

  const proposeBasicDetailsActions = useMemo(() => {
    // eslint-disable-next-line prefer-const
    let { disabled, tooltip } = getProposalProps("PROPOSE_BASIC_CHANGE");
    disabled = isProposeFindingsDisabled || disabled;
    return { disabled, tooltip };
  }, [getProposalProps, isProposeFindingsDisabled]);

  const onExecuteScan = useCallback(() => {
    const closeMessage = message.loading("Executing Scan...");
    const { promise, cancel } = ToolsIntegrationsAPI.executeScanFinding(
      finding.id
    );
    promise
      .then((res) => {
        message.success(res.data);
      })
      .catch((e) => console.error(e))
      .finally(() => closeMessage());
    return cancel;
  }, [finding]);

  const popup_menu_v2 = (
    <Menu>
      <Menu.Item
        disabled={isAssignProjectDisabled}
        onClick={() => setFindingsAndRedirect("/findings/changeProject")}
      >
        Assign <Vocabulary name="project" />
      </Menu.Item>
      {featureFlags.EXCEPTION_MODULE && (
        <Menu.Item
          disabled={isAddToExceptionDisabled}
          onClick={() => setFindingsAndRedirect("/findings/addToExcption")}
        >
          Add to <Vocabulary name="exception" />
        </Menu.Item>
      )}
      {featureFlags.PENTEST_MODULE && (
        <Menu.Item
          disabled={isAddToAssessmentDisabled}
          onClick={() => setFindingsAndRedirect("/findings/addToAssessment")}
        >
          Add to <Vocabulary name="assessment" />
        </Menu.Item>
      )}
      {featureFlags.EXECUTE_TOOL_SCAN && (
        <Menu.Item
          disabled={!SCAN_EXECUTE_SUPPORTED_TOOL.includes(finding?.toolName)}
          onClick={onExecuteScan}
        >
          Execute Scan
        </Menu.Item>
      )}
      <Menu.Divider />
      <Menu.Item
        disabled={isDetachTicketDisabled}
        onClick={() => setFindingsAndRedirect("/findings/detachTicket")}
      >
        Detach Ticket
      </Menu.Item>
      <Menu.Divider />
      <Menu.Item
        disabled={proposeFalsePositiveActions.disabled}
        onClick={() =>
          setFindingsAndRedirect("/findings/falsePositive?type=propose")
        }
      >
        <Tooltip title={proposeFalsePositiveActions.tooltip}>
          <span>
            Propose <Vocabulary name="falsePositive" />
          </span>
        </Tooltip>
      </Menu.Item>
      <Menu.Item
        disabled={proposeAcceptRiskActions.disabled}
        onClick={() =>
          setFindingsAndRedirect("/findings/acceptRisk?type=propose")
        }
      >
        <Tooltip title={proposeAcceptRiskActions.tooltip}>
          <span>
            Propose <Vocabulary name="acceptRisk" />
          </span>
        </Tooltip>
      </Menu.Item>
      <Menu.SubMenu title={"Propose Other"}>
        <Menu.Item
          disabled={proposeMitigatedActions.disabled}
          onClick={() =>
            setFindingsAndRedirect("/findings/mitigated?type=propose")
          }
        >
          <Tooltip
            title={
              proposeMitigatedActions.disabled ? (
                <>
                  {PROPOSED_ACTION_MAP.PROPOSE_MITIGATED} not available for this
                  finding
                </>
              ) : (
                ""
              )
            }
          >
            <span>
              <Vocabulary name="mitigated" />
            </span>
          </Tooltip>
        </Menu.Item>
        <Menu.Item
          disabled={proposeSuppressedActions.disabled}
          onClick={() =>
            setFindingsAndRedirect("/findings/suppressed?type=propose")
          }
        >
          <Tooltip title={proposeSuppressedActions.tooltip}>
            <span>
              <Vocabulary name="suppressed" />
            </span>
          </Tooltip>
        </Menu.Item>
        {!disabledFindingStatus.includes(FINDING_STATUS.TRIAGE) && (
          <Menu.Item
            disabled={proposeTriageActions.disabled}
            onClick={() =>
              setFindingsAndRedirect("/findings/triage?type=propose")
            }
          >
            <Tooltip title={proposeTriageActions.tooltip}>
              <span>
                <Vocabulary name="triageStatus" />
              </span>
            </Tooltip>
          </Menu.Item>
        )}
        {!disabledFindingStatus.includes(FINDING_STATUS.IN_PROGRESS) && (
          <Menu.Item
            disabled={proposeInProgressActions.disabled}
            onClick={() =>
              setFindingsAndRedirect("/findings/inProgress?type=propose")
            }
          >
            <Tooltip Tooltip title={proposeInProgressActions.tooltip}>
              <span>
                <Vocabulary name="inProgressStatus" />
              </span>
            </Tooltip>
          </Menu.Item>
        )}
        {!disabledFindingStatus.includes(FINDING_STATUS.CONTROLLED) && (
          <Menu.Item
            disabled={proposeControlledActions.disabled}
            onClick={() =>
              setFindingsAndRedirect("/findings/controlled?type=propose")
            }
          >
            <Tooltip title={proposeControlledActions.tooltip}>
              <span>
                <Vocabulary name="controlledStatus" />
              </span>
            </Tooltip>
          </Menu.Item>
        )}
        <Menu.Divider />
        <Menu.Item
          disabled={proposeCategoryActions.disabled}
          onClick={() =>
            setFindingsAndRedirect("/findings/category?type=propose")
          }
        >
          <Tooltip title={proposeCategoryActions.tooltip}>
            <span>
              <Vocabulary name="category" />
            </span>
          </Tooltip>
        </Menu.Item>
        <Menu.Item
          disabled={proposeSeverityActions.disabled}
          onClick={() =>
            setFindingsAndRedirect("/findings/severity?type=propose")
          }
        >
          <Tooltip title={proposeSeverityActions.tooltip}>
            <span>
              <Vocabulary name="severity" />
            </span>
          </Tooltip>
        </Menu.Item>
        <Menu.Item
          disabled={proposeDueDateActions.disabled}
          onClick={() =>
            setFindingsAndRedirect("/findings/dueDate?type=propose")
          }
        >
          <Tooltip title={proposeDueDateActions.tooltip}>
            <span>
              <Vocabulary name="resolution" /> Due Date
            </span>
          </Tooltip>
        </Menu.Item>
        <Menu.Item
          disabled={proposeAssigneeActions.disabled}
          onClick={() =>
            setFindingsAndRedirect("/findings/changeOwner?type=propose")
          }
        >
          <Tooltip title={proposeAssigneeActions.tooltip}>
            <span><Vocabulary name="owner" /></span>
          </Tooltip>
        </Menu.Item>
        <Menu.Item
          disabled={proposeBasicDetailsActions.disabled}
          onClick={() =>
            setFindingsAndRedirect("/findings/basicEdit?type=propose")
          }
        >
          <Tooltip title={proposeBasicDetailsActions.tooltip}>
            <span>Basic Information</span>
          </Tooltip>
        </Menu.Item>
      </Menu.SubMenu>
    </Menu>
  );

  const refresh = useCallback(() => onUpdate(true), [onUpdate]);

  return (
    <ACButtonGroup size="md">
      <Tooltip title={isFindingSourceJira ? "Tickets cannot be created for a finding when the source tool is a ticketing system." : "Create Ticket"} placement="bottom">
        <ACButtonGroupItem
          disabled={loading || isCreateTicketDisabled}
          onClick={() => setFindingsAndRedirect("/findings/createTicket")}
        >
          <MultiTicketIcon />
        </ACButtonGroupItem>
      </Tooltip>
      <Tooltip title="Refresh" placement="bottom">
        <ACButtonGroupItem onClick={refresh}>
          <RefreshIcon />
        </ACButtonGroupItem>
      </Tooltip>
      <Dropdown
        size="small"
        type="text"
        overlay={popup_menu_v2}
        trigger="click"
      >
        <ACButtonGroupItem disabled={loading}>
          <MenuIcon />
        </ACButtonGroupItem>
      </Dropdown>
    </ACButtonGroup>
  );
}
